/**
 * ManualContainer
 * --
 */
import React from 'react';
import ManualPresenter from './ManualPresenter';
import MessageAlert, { STORE_URL } from 'utils';
import { UserApi } from 'api';
import { useGetProducts } from 'hooks/ProductHooks';
import { useGetForwardingLocations } from 'hooks/ForwardingLocationsHooks';
import dayjs from 'dayjs';
import { useCreateManualShopOrder } from 'hooks/ShopOrderHooks';

const INITIAL_DELIVERY_ADDRESS = {
  delivery_address_id: null,
  recipient_postcode: '',
  recipient_address: '',
  recipient_address_detail: '',
  recipient_name: '',
  recipient_phone: '',
  request: '',
};

const INITIAL_PAYMENT_INFO = {
  payment_method: 'CARD',
  payment_state: 'COMPLETED',
  order_route: 'PC',
  payment_date: '',
};

const ManualContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { auth = 'ADMIN', defaultShop = null } = props;

  /* ===== Mutate ===== */
  const { createManualShopOrder } = useCreateManualShopOrder();

  /* ===== Data ===== */
  // 상품 데이터
  const { data: Products } = useGetProducts(
    {
      type: 'DELIVERY',
      is_show: 'Y',
      application_status: 'APPROVE',
      product_status: 'NORMAL',
      join_tables: '["product_file", "shop_information"]',
      ...(defaultShop ? { shop_information_id: defaultShop } : {}),
    },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        const newData = data.map((product) => {
          const thumbnail = product.product_file
            ? product.product_file.find((file) => file.type === 'THUMBNAIL')
            : null;

          return {
            ...product,
            thumbnail,
          };
        });

        return newData;
      },
    }
  );
  // 출고지 데이터
  const { data: Forwarding } = useGetForwardingLocations(
    {
      ...(defaultShop ? { shop_information_id: defaultShop } : {}),
    },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    }
  );

  /* ===== STATE ===== */
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // 찾을 회원 휴대폰 번호
  const [userPhoneNumb, setUserPhoneNumb] = React.useState('');
  // 주문장 생성할 회원
  const [selectedUser, setSelectedUser] = React.useState(null);
  // 주문 타입
  const [type, setType] = React.useState('DELIVERY');
  // 픽업지
  const [pickUp, setPickUp] = React.useState(null);
  // 선택 상품
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  // 상품 정보
  const [productInformation, setProductInformation] = React.useState([]);
  // 출고지별 상품 정보
  const [forwardingInformation, setForwardingInformation] = React.useState([]);
  // 배송지
  const [deliveryAddress, setDeliveryAddress] = React.useState(
    INITIAL_DELIVERY_ADDRESS
  );
  // 결제 정보
  const [paymentInfo, setPaymentInfo] = React.useState(INITIAL_PAYMENT_INFO);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * ANCHOR 회원 검색
   * --
   */
  const handleSearchUser = async () => {
    if (userPhoneNumb.length < 12)
      return MessageAlert.info(
        '휴대폰 번호 12자리 또는 13자리를 모두 입력해주세요!'
      );

    let res = null;
    try {
      setIsLoading(true);

      const query = {
        user_phone: userPhoneNumb,
      };
      const { status, data = [] } = await UserApi.getUsers(query);

      if (status !== 200)
        return MessageAlert.warning('회원찾기에 실패하였습니다.');

      if (!data.length) return MessageAlert.info('존재하지 않는 회원입니다.');

      res = data[0];
    } catch (err) {
      console.error('[ManualContainer][handleSearchUser] Error!! ', err);
      MessageAlert.error('회원 찾기 에러!');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        setSelectedUser(res);
      }, 500);
    }
  };

  /**
   * ANCHOR 데이터 바인딩
   * --
   * @param {string} type
   * @param {func} func
   * @param {any} value
   * @param {string | null} target
   * @returns
   */
  const handleChange = (type = 'object', func, value, target = null) => {
    switch (type) {
      case 'object':
        return func((prev) => ({
          ...prev,
          [target]: value,
        }));
      case 'string':
      case 'number':
        return func(value);
      default:
        return;
    }
  };

  /**
   * ANCHOR 주문장 상품 핸들러
   * --
   * @param {string} t
   * @param {object | number | null} d
   * @param {func} callback
   * @returns
   */
  const handleProductInformation = (
    t = 'ADD',
    d = null,
    callback = () => { }
  ) => {
    const type = t.toLocaleUpperCase();

    switch (type) {
      case 'ADD': {
        if (!d)
          return MessageAlert.warning('상품 추가 중 오류가 발생하였습니다.');

        // 같은 종류의 상품이 존재하는지 체크
        const addIndex =
          productInformation.findIndex(
            (item) => item.product_key === d.product_key
          ) ?? -1;

        if (addIndex === -1) {
          setProductInformation((prev) => [...prev, d]);
        } else {
          // 기존 상품
          const data = productInformation[addIndex];

          const { num } = data;

          let newCount = num + d.num;

          if (newCount > 99) {
            newCount = 99;
            MessageAlert.info('최대 99개의 상품까지 주문 가능합니다.');
          }

          const newData = {
            ...data,
            num: newCount,
          };

          setProductInformation((prev) => [
            ...prev.slice(0, addIndex),
            newData,
            ...prev.slice(addIndex + 1),
          ]);
        }

        callback && callback();
        setSelectedProduct(null);
        break;
      }
      case 'REMOVE': {
        const removeIndex =
          productInformation.findIndex((item) => item.product_key === d) ?? -1;

        if (removeIndex === -1) return;

        setProductInformation((prev) => [
          ...prev.slice(0, removeIndex),
          ...prev.slice(removeIndex + 1),
        ]);

        callback && callback();
        return;
      }
      case 'RESET': {
        setSelectedProduct(null);
        setProductInformation([]);
        setForwardingInformation([]);
        setDeliveryAddress(INITIAL_DELIVERY_ADDRESS);
        setPaymentInfo(INITIAL_PAYMENT_INFO);
        return;
      }
      case 'UPDATE': {
        if (!d)
          return MessageAlert.warning('상품 수정 중 오류가 발생하였습니다.');

        // 기존 아이템 제거
        const removeList = [...productInformation].filter(
          (product) => product?.product_key !== d?.origin_key
        );

        // 업데이트 하려는 상품과 같은 옵션을 가진 상품이 존재하는지 체크
        const updateIndex = removeList.findIndex(
          (product) => product?.product_key === d?.product_key
        );

        if (updateIndex === -1) {
          setProductInformation([...removeList, d]);
        } else {
          // 기존 같은 옵션 상품
          const data = removeList[updateIndex];

          const { num } = data;

          let newCount = num + d.num;

          if (newCount > 99) {
            newCount = 99;
            MessageAlert.info('최대 99개의 상품까지 주문 가능합니다.');
          }

          const newData = {
            ...data,
            num: newCount,
          };

          setProductInformation([
            ...removeList.slice(0, updateIndex),
            newData,
            ...removeList.slice(updateIndex + 1),
          ]);
        }

        callback && callback();
        setSelectedProduct(null);
        return;
      }
      case 'CANCEL': {
        setSelectedProduct(null);
      }
      default:
        return;
    }
  };

  /**
   * 상품 정보 변경
   * --
   * @param {string} k
   * @param {string} t
   * @param {string | number} v
   * @returns
   */
  const handleProductPrice = (k, t, v) => {
    const fid = productInformation.findIndex(
      (product) => product?.product_key === k
    );

    if (fid === -1) return;

    setProductInformation((prev) => [
      ...prev.slice(0, fid),
      {
        ...prev[fid],
        [t]: v,
      },
      ...prev.slice(fid + 1),
    ]);
  };

  /**
   * ANCHOR 적용 배송비 수정
   * --
   * @param {number} idx
   * @param {number} price
   */
  const handleDeliveryPriceChange = (idx, price) => {
    const fid = forwardingInformation.findIndex(
      (item) => item.forwarding_location_id === idx
    );

    if (fid === -1) return;

    setForwardingInformation((prev) => [
      ...prev.slice(0, fid),
      {
        ...prev[fid],
        apply_delivery_price: price,
      },
      ...prev.slice(fid + 1),
    ]);
  };

  /**
   * ANCHOR 수동 주문장 생성 벨류 체크
   * --
   */
  const handleValidateCheck = () => {
    // NOTE: 회원 선택 체크
    if (!selectedUser) {
      MessageAlert.info('주문한 회원을 선택해주세요!');
      return false;
    }

    // NOTE: 스토어픽업 픽업지 체크
    if (type === 'STORE_PICKUP' && !pickUp) {
      MessageAlert.info('픽업지를 선택해주세요!');
      return false;
    }

    // NOTE: 상품 선택 체크
    if (!productInformation.length) {
      MessageAlert.info('주문 상품을 선택해주세요!');
      return false;
    }

    // NOTE: 배송지 체크
    if (type === 'DELIVERY') {
      const {
        recipient_postcode,
        recipient_address,
        recipient_address_detail,
        recipient_name,
        recipient_phone,
      } = deliveryAddress;

      if (!recipient_postcode || !recipient_address) {
        MessageAlert.info('배송지를 검색해주세요!');
        return false;
      }

      if (!recipient_address_detail) {
        MessageAlert.info('배송지 상세주소를 입력해주세요!');
        return false;
      }

      if (!recipient_name) {
        MessageAlert.info('수령자 명을 입력해주세요!');
        return false;
      }

      if (!recipient_phone) {
        MessageAlert.info('수령자 연락처를 입력해주세요!');
        return false;
      }
    }

    // NOTE: 결제정보 체크
    const { payment_method, order_route, payment_date } = paymentInfo;
    if (!payment_method) {
      MessageAlert.info('결제수단을 선택해주세요!');
      return false;
    }
    if (!order_route) {
      MessageAlert.info('주문경로를 선택해주세요!');
      return false;
    }
    if (!payment_date) {
      MessageAlert.info('상품 주문일 선택해주세요!');
      return false;
    }

    return true;
  };

  /**
   * ANCHOR 수동주문장 생성
   * --
   */
  const handleSubmit = async () => {
    // 유효성 검사
    if (!handleValidateCheck()) return;

    const confirm = window.confirm(
      '주문장을 생성하시겠습니까?\n수동 주문장은 결제 취소가 불가능하며 매출에 반영됩니다.'
    );

    if (!confirm) return;

    let res = false;
    try {
      setIsLoading(true);

      // 주문 데이터 정제
      const orderData = forwardingInformation.map((item) => {
        const {
          final_discount_price,
          final_reserve_price,
          total_price,
          forwarding_location_id,
          shop_information_id,
          product_information,
          apply_delivery_price,
        } = item;

        return {
          user_id: selectedUser.user_id,
          shop_information_id,
          type,
          forwarding_location_id:
            type === 'STORE_PICKUP'
              ? pickUp.forwarding_location_id
              : forwarding_location_id,
          delivery_address: type === 'STORE_PICKUP' ? pickUp : deliveryAddress,
          total_price,
          final_reward_point: final_reserve_price,
          final_discount: -final_discount_price,
          final_price:
            type === 'STORE_PICKUP' ? total_price - final_discount_price
              : total_price - final_discount_price + apply_delivery_price,
          final_delivery_price:
            type === 'STORE_PICKUP' ? 0 : apply_delivery_price,
          use_jm_pay:
            paymentInfo.payment_method === 'JM_PAY'
              ? -(total_price - final_discount_price + apply_delivery_price)
              : 0,
          ...paymentInfo,
          payment_completion_date: paymentInfo.payment_date,
          created_at: paymentInfo.payment_date,
          payment_serial_number: `${dayjs
            .unix(paymentInfo.payment_date)
            .format(
              'YYYYMMDDHHmmss'
            )}-F${forwarding_location_id}M${dayjs().unix()}`,
          product_information: product_information.map((product) => {
            const {
              discount_price,
              product_option,
              commission_rate,
              commission_type,
              thumbnail,
              product_file, // 제외
              shop_information, // 제외
              ...rest
            } = product;

            return {
              ...rest,
              thumbnail: thumbnail
                ? `${STORE_URL}/${thumbnail?.file_url}`
                : null,
              discount_price: -discount_price,
              commission: {
                type: 'MANUAL',
                commission_rate,
                commission_type,
              },
              payment_price:
                (rest.price + rest.option_price) * rest.num - discount_price,
              product_option: !product_option
                ? null
                : product_option.map((option) => {
                  const { option: childOption, ...options } = option;

                  return {
                    ...options,
                    option: childOption.filter((op) => op.selected),
                  };
                }),
            };
          }),
        };
      });

      const { status, message } = await createManualShopOrder({
        shop_order: orderData,
      });

      if (status !== 200) {
        res = false;
        console.log(message);

        return;
      }

      res = true;
    } catch (err) {
      console.error('[ManualContainer][handleSubmit] Error!! ', err);
      MessageAlert.error('주문장 생성 에러!');
    } finally {
      setTimeout(() => {
        if (res) {
          handleProductInformation('RESET');
          setSelectedUser(null);
          setUserPhoneNumb('');
          MessageAlert.success('주문장이 성공적으로 생성되었습니다.');
        } else {
          MessageAlert.warning('수동 주문장생성에 실패하였습니다.');
        }
        setIsLoading(false);
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * ANCHOR 픽업지 정제
   * --
   */
  const PickUpLocation = React.useMemo(() => {
    if (!Forwarding.length) return [];

    const pickup = Forwarding.filter(
      (forwarding) => forwarding.type === 'RECEIVE'
    );

    return pickup;
  }, [Forwarding]);

  /**
   * ANCHOR 상품목록 정제
   * --
   */
  const ProductList = React.useMemo(() => {
    if (!Products.length) return [];

    switch (type) {
      case 'DELIVERY':
        return Products;
      case 'STORE_PICKUP':
        return Products.filter((product) => {
          const { receive_ids } = product;

          if (!receive_ids) return false;
          return receive_ids.some(
            (receive) =>
              receive.forwarding_location_id === pickUp?.forwarding_location_id
          );
        });
      default:
        return [];
    }
  }, [Products, type, pickUp]);

  /* ===== Effect ===== */
  /**
   * ANCHOR 출고지별 상품 그룹화 정제
   * --
   */
  React.useEffect(() => {
    if (!Forwarding.length || !productInformation.length) return;

    const groupProducts = productInformation.reduce((acc, cur) => {
      const productPrice = (cur.price + cur.option_price) * cur.num;

      const fid = acc.findIndex(
        (item) => item.forwarding_location_id === cur.forwarding_location_id
      );

      if (fid === -1) {
        const findForwarding = Forwarding.find(
          (forward) =>
            forward.forwarding_location_id === cur.forwarding_location_id
        );

        const newData = {
          ...findForwarding,
          total_price: productPrice,
          final_discount_price: cur.discount_price ?? 0,
          final_reserve_price: cur.reserve_price ?? 0,
          apply_delivery_price:
            productPrice >= findForwarding?.free_delivery_price
              ? 0
              : findForwarding?.delivery_price,
          product_information: [cur],
        };
        acc.push(newData);
      } else {
        const total = acc[fid].total_price + productPrice;
        const { free_delivery_price, delivery_price } = acc[fid];

        const newData = {
          ...acc[fid],
          total_price: total,
          final_discount_price:
            acc[fid].final_discount_price + (cur.discount_price ?? 0),
          final_reserve_price:
            acc[fid].final_reserve_price + (cur.reserve_price ?? 0),
          apply_delivery_price:
            total >= free_delivery_price ? 0 : delivery_price,
          product_information: [...acc[fid].product_information, cur],
        };

        acc = [...acc.slice(0, fid), newData, ...acc.slice(fid + 1)];
      }

      return acc;
    }, []);

    setForwardingInformation(groupProducts);
  }, [Forwarding, productInformation]);

  /* ===== Props ===== */
  /**
   * ANCHOR Props
   * --
   */
  const PROPS = {
    auth,
    user: selectedUser,
    resetUser: () => setSelectedUser(null),
    isLoading,
    userPhoneNumb,
    setUserPhoneNumb,
    onChange: handleChange,
    onSearchUser: handleSearchUser,
    type,
    setType,
    receives: PickUpLocation,
    pickUp,
    setPickUp,
    products: ProductList,
    selectedProduct,
    setSelectedProduct,
    productInformation,
    onProductInformation: handleProductInformation,
    onProductPrice: handleProductPrice,
    forwardingInformation,
    onDeliveryPriceChange: handleDeliveryPriceChange,
    deliveryAddress,
    setDeliveryAddress,
    paymentInfo,
    setPaymentInfo,
    onSubmit: handleSubmit,
  };
  /* ===== RENDER ===== */
  return (
    <>
      <ManualPresenter {...PROPS} />
    </>
  );
};

export default ManualContainer;
